import { useQuery } from '@tanstack/react-query';
import { CONTEXT_SECTION_TITLES, type ContextSection } from '@lixionary-jira/domain';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge, StatusBadge } from '../../ui/Badge';
import { Button, LinkButton } from '../../ui/Button';
import { Alert, ErrorAlert, QueryView } from '../../ui/feedback';
import { Field, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { CandidateForm, fromBinding, toPayload } from './CandidateForm';
import type { ReleaseDetail } from './types';

/** WF-10–15: binding, readiness, reservations, approval history, attempts and rollback links. */
export function ReleaseDetailPage() {
  const { key, releaseId } = useParams();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['release', key, releaseId],
    queryFn: () => api.get<ReleaseDetail>(`/projects/${key}/releases/${releaseId}`),
  });
  usePageTitle(q.data ? `${q.data.project.name} · Release ${q.data.displayId}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Releases', to: `/projects/${key}/releases` },
    { label: releaseId ?? '' },
  ]);
  const [dialog, setDialog] = useState<'edit' | 'request' | 'begin' | 'cancel' | null>(null);
  const [text, setText] = useState('');
  const base = `/projects/${key}/releases/${releaseId}`;
  const inv = [['release', key, releaseId], ['releases', key], ['releasable', key], ['approvals']];
  const request = useAction(
    () => api.post(`${base}/request-approval`, { expectedVersion: q.data!.version, rationale: text }),
    { success: 'Approval requested', invalidate: inv, onSuccess: () => setDialog(null) },
  );
  const begin = useAction(
    () =>
      api.post<{ attempt: string }>(`${base}/begin`, {
        expectedVersion: q.data!.version,
        approvalRequestId: q.data!.viewer.approvalToUse!.id,
      }),
    {
      success: (r) => `${r.attempt} registered — deploy externally now`,
      invalidate: inv,
      onSuccess: (r) => navigate(`${base}/attempts/${r.attempt}`),
    },
  );
  const cancel = useAction(
    () => api.post(`${base}/cancel`, { expectedVersion: q.data!.version, reason: text }),
    { success: 'Release cancelled', invalidate: inv, onSuccess: () => setDialog(null) },
  );
  return (
    <div className="page">
      <QueryView query={q}>
        {(r) => {
          const b = r.candidate?.binding;
          const latest = r.attempts[0];
          return (
            <>
              <PageHeader
                title={`Release ${r.displayId}`}
                subtitle={r.title}
                actions={
                  <>
                    <StateBadge state={r.state} />
                    {r.viewer.canEdit && (
                      <Button size="sm" variant="secondary" onClick={() => setDialog('edit')}>
                        {r.state === 'failed' ? 'Prepare retry' : 'Edit candidate'}
                      </Button>
                    )}
                    {r.viewer.canCancel && (
                      <Button size="sm" variant="danger-outline" onClick={() => setDialog('cancel')}>
                        Cancel release
                      </Button>
                    )}
                    {r.viewer.canRollback && (
                      <LinkButton size="sm" to={`${base}/rollbacks/new`}>
                        Prepare rollback
                      </LinkButton>
                    )}
                    {r.state === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => setDialog('request')}
                        disabled={!r.viewer.canRequestApproval}
                      >
                        Request approval
                      </Button>
                    )}
                    {r.viewer.canBegin && (
                      <Button size="sm" onClick={() => setDialog('begin')}>
                        Start approved attempt
                      </Button>
                    )}
                  </>
                }
              />
              {r.problems.length > 0 && (
                <Alert tone="warning" title="This candidate cannot be approved as is">
                  <ul>
                    {r.problems.map((p, i) => (
                      <li key={i}>{p.message}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {(r.state === 'deploying' || r.state === 'unknown') && latest && (
                <Alert
                  tone={r.state === 'unknown' ? 'warning' : 'info'}
                  title={
                    r.state === 'unknown' ? 'Outcome unknown — environment locked' : 'Deployment in progress'
                  }
                >
                  <span>
                    Bindings and included scopes are locked until ATT-{latest.number} is reconciled. Editing
                    tickets cannot undo external execution.{' '}
                    <Link to={`${base}/attempts/ATT-${latest.number}`}>Open ATT-{latest.number}</Link>
                  </span>
                </Alert>
              )}
              {r.state === 'failed' && (
                <Alert tone="error" title="Last attempt failed">
                  Tickets stay Pending release and remain reserved. Prepare a retry (new candidate and fresh
                  approval) or cancel the release.
                </Alert>
              )}
              {r.state === 'awaiting_approval' && (
                <Alert tone="info">
                  Waiting for an admin to review approval {r.approvals[0]?.displayId}.{' '}
                  <Link to={`/approvals/${r.approvals[0]?.displayId}`}>Open request</Link>
                </Alert>
              )}
              {r.viewer.approvalToUse && (
                <Alert tone="success" title="Approved for one attempt">
                  Use {r.viewer.approvalToUse.displayId} before{' '}
                  <Timestamp value={r.viewer.approvalToUse.useBy} />. A retry needs a new approval.
                </Alert>
              )}
              <div className="split">
                <div className="stack">
                  {b && (
                    <Card
                      title={`Candidate ${r.candidate!.number}`}
                      subtitle={
                        <>
                          Binding <span className="mono">{r.candidate!.bindingHash.slice(0, 12)}</span> ·{' '}
                          <Timestamp value={r.candidate!.createdAt} />
                        </>
                      }
                    >
                      <KeyValue
                        items={[
                          [
                            'Environment',
                            <span key="e">
                              {b.environmentName}{' '}
                              {b.isCompletionTarget ? (
                                <Badge tone="success" dot={false}>
                                  Completion target
                                </Badge>
                              ) : (
                                <Badge dot={false}>Not completion</Badge>
                              )}
                            </span>,
                          ],
                          [
                            'Revision',
                            <span key="r" className="mono">
                              {b.sourceRevision}
                            </span>,
                          ],
                          [
                            'Artifact',
                            b.artifactDigest ? (
                              <span className="mono small">{b.artifactDigest}</span>
                            ) : (
                              'Source revision binding'
                            ),
                          ],
                          [
                            'Repository',
                            <span key="rp" className="mono small">
                              {b.repositoryUrl ?? '—'}
                            </span>,
                          ],
                          [
                            'Executor',
                            `${b.executor.name}${b.executor.kind === 'ai_service' ? ' (AI)' : ''}`,
                          ],
                          [
                            'Context',
                            Object.entries(r.candidate!.snapshot?.contextVersions ?? {})
                              .map(
                                ([s, n]) =>
                                  `${CONTEXT_SECTION_TITLES[s as ContextSection]} ${n ? `v${n}` : '—'}`,
                              )
                              .join(' · '),
                          ],
                          ['Exceptions', b.exceptions?.length ? b.exceptions.join(', ') : 'None'],
                        ]}
                      />
                      <span className="small strong">Integration evidence</span>
                      <Markdown>{b.integrationEvidence ?? ''}</Markdown>
                      <span className="small strong">Deployment plan</span>
                      <Markdown>{b.deploymentPlan ?? ''}</Markdown>
                      <span className="small strong">Required checks</span>
                      <ul className="checklist">
                        {b.verificationChecks.map((c) => (
                          <li key={c}>
                            {c}
                            {b.manualChecks?.includes(c) && (
                              <span className="small muted"> (manual, recorded after the deploy)</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      <span className="small strong">Rollback plan</span>
                      <Markdown>{b.rollbackPlan ?? ''}</Markdown>
                    </Card>
                  )}
                  <Card title="Tickets and readiness">
                    <div className="table-wrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Ticket</th>
                            <th>Status</th>
                            <th>Scope rev</th>
                            <th>Readiness revision</th>
                            <th>Reservation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.tickets.map((t) => {
                            const s = r.candidate?.snapshot?.tickets.find((x) => x.key === t.key);
                            return (
                              <tr key={t.key}>
                                <td>
                                  <Link to={`/projects/${key}/tickets/${t.number}`} className="mono">
                                    {t.key}
                                  </Link>{' '}
                                  {t.title}
                                </td>
                                <td>
                                  <StatusBadge
                                    status={t.status as never}
                                    resolution={t.resolution as never}
                                  />
                                </td>
                                <td>{s?.scopeRevision ?? '—'}</td>
                                <td className="mono small">{s?.sourceRevision?.slice(0, 12) ?? '—'}</td>
                                <td>
                                  {t.reserved ? (
                                    <Badge tone="info" dot={false}>
                                      Reserved
                                    </Badge>
                                  ) : (
                                    <span className="muted small">Released</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  <Card title="Attempts">
                    {r.attempts.length === 0 ? (
                      <span className="small muted">No attempts yet.</span>
                    ) : (
                      <ul className="checklist">
                        {r.attempts.map((a) => (
                          <li key={a.id} style={{ justifyContent: 'space-between' }}>
                            <Link to={`${base}/attempts/ATT-${a.number}`} className="mono">
                              ATT-{a.number}
                            </Link>
                            <span className="row nowrap">
                              <StateBadge state={a.state} />
                              {a.provenance && <StateBadge state={a.provenance} />}
                              <span className="small muted">
                                <Timestamp value={a.registeredAt} />
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </div>
                <div className="stack">
                  <Card title="Approvals" tight>
                    {r.approvals.length === 0 ? (
                      <span className="small muted">None yet.</span>
                    ) : (
                      <ul className="checklist">
                        {r.approvals.map((a) => (
                          <li key={a.id} style={{ flexDirection: 'column', gap: 2 }}>
                            <span>
                              <Link to={`/approvals/${a.displayId}`} className="mono">
                                {a.displayId}
                              </Link>{' '}
                              · candidate {a.candidateNumber}{' '}
                              <StateBadge
                                state={a.consumedAt ? 'released' : a.state}
                                label={a.consumedAt ? 'Used' : undefined}
                              />
                            </span>
                            <span className="small muted">
                              Requested by <ActorName actor={a.requester} />
                              {a.decidedBy && (
                                <>
                                  {' '}
                                  · decided by <ActorName actor={a.decidedBy} />
                                </>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                  <Card title="Rollbacks" tight>
                    {r.rollbacks.length === 0 ? (
                      <span className="small muted">None.</span>
                    ) : (
                      <ul className="checklist">
                        {r.rollbacks.map((rb) => (
                          <li key={rb.id} style={{ justifyContent: 'space-between' }}>
                            <Link to={`${base}/rollbacks/${rb.displayId}`}>
                              <span className="mono">{rb.displayId}</span> {rb.title}
                            </Link>
                            <StateBadge state={rb.state} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                  <Card title="History" tight>
                    <KeyValue
                      items={[
                        ['Created', <Timestamp key="c" value={r.createdAt} />],
                        ['By', <ActorName key="b" actor={r.createdBy} />],
                        [
                          'Candidates',
                          r.candidateHistory
                            .map((c) => `#${c.number} ${c.sourceRevision.slice(0, 8)}`)
                            .join(', '),
                        ],
                      ]}
                    />
                  </Card>
                </div>
              </div>
              {dialog === 'edit' && <EditCandidate r={r} onClose={() => setDialog(null)} inv={inv} />}
              {dialog === 'request' && (
                <ConfirmDialog
                  title={`Request approval for ${r.displayId}?`}
                  confirmLabel="Request approval"
                  onClose={() => setDialog(null)}
                  onConfirm={() => request.mutate(undefined)}
                  pending={request.isPending}
                  error={request.error}
                >
                  <p>The candidate freezes. Any later change makes the request stale.</p>
                  <Field label="Rationale" optional>
                    {(p) => (
                      <Textarea {...p} rows={3} value={text} onChange={(e) => setText(e.target.value)} />
                    )}
                  </Field>
                </ConfirmDialog>
              )}
              {dialog === 'begin' && (
                <ConfirmDialog
                  title={`Start attempt ATT-${r.attemptCount + 1}?`}
                  confirmLabel="Register attempt and consume approval"
                  onClose={() => setDialog(null)}
                  onConfirm={() => begin.mutate(undefined)}
                  pending={begin.isPending}
                  error={begin.error}
                >
                  <p>
                    This registers the attempt and consumes approval {r.viewer.approvalToUse?.displayId}. The
                    application does not deploy anything: run the deployment plan yourself, then report the
                    outcome on the attempt page.
                  </p>
                </ConfirmDialog>
              )}
              {dialog === 'cancel' && (
                <ConfirmDialog
                  title={`Cancel ${r.displayId}?`}
                  confirmLabel="Yes, cancel"
                  variant="danger"
                  onClose={() => setDialog(null)}
                  onConfirm={() => cancel.mutate(undefined)}
                  pending={cancel.isPending}
                  error={cancel.error}
                  disabled={!text.trim()}
                >
                  <p>
                    Reservations are released and outstanding approvals become stale. Previous attempts stay
                    in history.
                  </p>
                  <Field label="Reason">
                    {(p) => (
                      <Textarea {...p} rows={2} value={text} onChange={(e) => setText(e.target.value)} />
                    )}
                  </Field>
                </ConfirmDialog>
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

function EditCandidate({ r, onClose, inv }: { r: ReleaseDetail; onClose: () => void; inv: unknown[][] }) {
  const [v, setV] = useState(fromBinding(r.candidate!.binding));
  const save = useAction(
    () =>
      api.put(`/projects/${r.project.key}/releases/${r.displayId}`, {
        ...toPayload(v),
        expectedVersion: r.version,
      }),
    { success: 'New candidate saved', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title={r.state === 'failed' ? `Prepare retry for ${r.displayId}` : `Edit ${r.displayId}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate(undefined)} loading={save.isPending}>
            Save candidate {(r.candidate?.number ?? 0) + 1}
          </Button>
        </>
      }
    >
      <Alert tone="info">
        Saving creates a new candidate and returns the release to draft. Outstanding approvals become stale;
        previous candidates and attempts are preserved.
      </Alert>
      <CandidateForm
        projectKey={r.project.key}
        value={v}
        onChange={setV}
        currentTickets={r.tickets.filter((t) => t.reserved).map((t) => t.key)}
      />
      <ErrorAlert error={save.error} />
    </Modal>
  );
}
